import { createContext, type PropsWithChildren, useContext, useMemo } from 'react';

import { type SearchFlags } from '~/apps/product-plus/search-domain/types';

export type FeatureFlagProviderProps = {
  featureToggleDefinitions: {
    description?: string;
    enabled: boolean;
    name: string;
  }[];
};

export type FeatureFlagProviderData = FeatureFlagProviderProps;

export const featureFlags = {
  DISABLE_HEADLESS_OFFERS_I18N_CACHE: 'disable-headless-offers-i18n-cache',
  DISABLE_BEST_BETS_WIDGET: 'disable-best-bets-widget',
  DISABLE_GOOGLE_TAG_MANAGER: 'disable-google-tag-manager',
  DISABLE_GRID_TAB_STATS: 'disable-grid-tab-stats',
  DISABLE_MARKET_GROUPS_API: 'disable-market-groups-api',
  DISABLE_OFFER_WIDGETS: 'disable-offer-widgets',
  DISABLE_SENTRY: 'disable-sentry',
  DISABLE_VWO: 'disable-vwo',
  RENDER_GTM_POST_HYDRATION: 'render-gtm-post-hydration',
  SUBS__FINGERPRINTING: 'subs__fingerprinting',
  SUBS__PLUS__ALL_WNBA: 'subs__plus__all_wnba',
  SUBS__PLUS__HERO_PRICING: 'subs__plus__hero__pricing',
  SUBS__PLUS__INTERNAL_SEARCH_TOOLS: 'subs__plus__internal_search_tools',
  SUBS__PLUS__LAST_10_PERSISTENT_SORT: 'subs__plus__last_10_persistent_sort',
  SUBS__PLUS__LINE_REDUCTION: 'subs__plus__line_reduction',
  SUBS__PLUS__NEW_CATEGORY_GROUPS: 'subs__plus__new_category_groups',
  SUBS__PLUS__PADLOCKING: 'subs__plus__padlocking',
  SUBS__PLUS__Q4_PROPS: 'subs__plus__q4_props',
  SUBS__PLUS__SOCCER_BETTING_SPLITS: 'subs__plus__soccer_betting_splits',
  SUBS__PLUS__SOCCER_EPL_ONLY: 'subs__plus__soccer_epl_only',
  SUBS__PLUS__SOCCER_EV_TRENDS: 'subs__plus__soccer_ev_trends',
  SUBS__PLUS__SOCCER_TRENDS: 'subs__plus__soccer_trends',
  SUBS__PLUS__STATS_TABLE: 'subs__plus__stats_table',
  SUBS__PLUS__TEXT_SEARCH: 'subs__plus__text_search',
  SUBS__PLUS__TIPSTERS: 'subs__plus__tipsters',
  SUBS__PLUS__TRENDS__BARCHART: 'subs__plus__trends__barchart',
  RENDER_BEST_BETS_WIDGET_FULL_WIDTH: 'render-best-bets-widget-full-width',
  RENDER_ONBOARDING_AS_MYOC_LOGIN_CTA: 'render-onboarding-as-myoc-login-cta',
};

export const FeatureFlagContext = createContext<FeatureFlagProviderData>(undefined as any as FeatureFlagProviderData);
FeatureFlagContext.displayName = 'FeatureFlagContext';

export const FeatureFlagProvider = ({
  children,
  featureToggleDefinitions,
  ...props
}: PropsWithChildren<FeatureFlagProviderData>) => {
  const value = useMemo(
    () => ({
      featureToggleDefinitions,
    }),
    [featureToggleDefinitions],
  );

  return (
    <FeatureFlagContext.Provider value={value} {...props}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlagContext = () => {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error('useFeatureFlagContext can only be used inside of FeatureFlagProvider');
  }

  return context;
};

export function useFeatureFlag<K extends keyof typeof featureFlags>(name: (typeof featureFlags)[K]) {
  const context = useFeatureFlagContext();

  const featureToggle = context.featureToggleDefinitions.find(featureToggle => featureToggle.name === name);

  return featureToggle?.enabled ?? false;
}

function isFeatureFlagEnabled(
  featureToggleDefinitions: FeatureFlagProviderProps['featureToggleDefinitions'],
  name: string,
): boolean {
  return featureToggleDefinitions.some(featureToggle => featureToggle.name === name && featureToggle.enabled);
}

export function getSearchFlags(
  featureToggleDefinitions: FeatureFlagProviderProps['featureToggleDefinitions'],
): SearchFlags {
  return {
    allWnba: isFeatureFlagEnabled(featureToggleDefinitions, featureFlags.SUBS__PLUS__ALL_WNBA),
    internalSearchTools: isFeatureFlagEnabled(featureToggleDefinitions, featureFlags.SUBS__PLUS__INTERNAL_SEARCH_TOOLS),
    last10PersistentSort: isFeatureFlagEnabled(
      featureToggleDefinitions,
      featureFlags.SUBS__PLUS__LAST_10_PERSISTENT_SORT,
    ),
    lineReduction: isFeatureFlagEnabled(featureToggleDefinitions, featureFlags.SUBS__PLUS__LINE_REDUCTION),
    newCategoryGroups: isFeatureFlagEnabled(featureToggleDefinitions, featureFlags.SUBS__PLUS__NEW_CATEGORY_GROUPS),
    soccerBettingSplits: isFeatureFlagEnabled(featureToggleDefinitions, featureFlags.SUBS__PLUS__SOCCER_BETTING_SPLITS),
    soccerEplOnly: isFeatureFlagEnabled(featureToggleDefinitions, featureFlags.SUBS__PLUS__SOCCER_EPL_ONLY),
    soccerTrends: isFeatureFlagEnabled(featureToggleDefinitions, featureFlags.SUBS__PLUS__SOCCER_TRENDS),
  };
}
